// Scrollspy for the chapter navigation.
//
// The app shell (AppLayout) scrolls its own <main> element — NOT the window and
// NOT the room's div. So we (a) navigate with native scrollIntoView, which
// finds the correct scroll ancestor automatically and respects each section's
// CSS scroll-margin-top, and (b) run the spy against that same real scroller.

import { useCallback, useEffect, useRef } from "react"

/** Nearest scrollable ancestor, or the document scroller. */
function getScrollParent(el: HTMLElement | null): HTMLElement {
  let n: HTMLElement | null = el?.parentElement ?? null
  while (n) {
    const s = getComputedStyle(n)
    if (/(auto|scroll)/.test(s.overflowY) && n.scrollHeight > n.clientHeight) return n
    n = n.parentElement
  }
  return (document.scrollingElement as HTMLElement) ?? document.documentElement
}

export function useScrollSpy(args: {
  containerRef: React.RefObject<HTMLElement | null>
  chapter: string
  setChapter: (id: string) => void
  chapterIds: string[]
  ready: boolean
}) {
  const { containerRef, chapter, setChapter, chapterIds, ready } = args

  const chapterRef = useRef(chapter); chapterRef.current = chapter
  const setChapterRef = useRef(setChapter); setChapterRef.current = setChapter
  const idsRef = useRef(chapterIds); idsRef.current = chapterIds

  const suppress = useRef(false)
  const suppressTimer = useRef<number | undefined>(undefined)

  const scrollToChapter = useCallback((id: string) => {
    const el = document.getElementById(`chapter-${id}`)
    if (!el) return
    suppress.current = true
    window.clearTimeout(suppressTimer.current)
    // Native scroll: targets the real scroll ancestor and honors scroll-margin-top.
    el.scrollIntoView({ behavior: "smooth", block: "start" })
    suppressTimer.current = window.setTimeout(() => { suppress.current = false }, 700)
  }, [])

  // Spy on the real scroll parent.
  useEffect(() => {
    if (!ready) return
    const scroller = getScrollParent(containerRef.current)
    let raf = 0
    const onScroll = () => {
      if (suppress.current || raf) return
      raf = requestAnimationFrame(() => {
        raf = 0
        const railH = parseFloat(getComputedStyle(containerRef.current ?? document.body).getPropertyValue("--room-rail-h")) || 96
        const line = (scroller.getBoundingClientRect?.().top ?? 0) + railH + 24
        let active = idsRef.current[0]
        for (const id of idsRef.current) {
          const el = document.getElementById(`chapter-${id}`)
          if (el && el.getBoundingClientRect().top <= line) active = id
        }
        if (active && active !== chapterRef.current) setChapterRef.current(active)
      })
    }
    scroller.addEventListener("scroll", onScroll, { passive: true })
    return () => { scroller.removeEventListener("scroll", onScroll); if (raf) cancelAnimationFrame(raf) }
  }, [containerRef, ready])

  // Deep-link on mount.
  const didDeepLink = useRef(false)
  useEffect(() => {
    if (didDeepLink.current || !ready) return
    didDeepLink.current = true
    const target = chapterRef.current
    if (target && target !== idsRef.current[0] && idsRef.current.includes(target)) {
      requestAnimationFrame(() => requestAnimationFrame(() => scrollToChapter(target)))
    }
  }, [ready, scrollToChapter])

  return { scrollToChapter }
}
